'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function AverageCalculator() {
  const t = useTranslations("tools.averageCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [numberInput, setNumberInput] = useState('10, 2, 38, 23, 38, 23, 21');
  const [results, setResults] = useState<{
    average: number;
    sum: number;
    count: number;
    numbers: number[];
  } | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const parseNumbers = (input: string): number[] => {
    const cleaned = input.replace(/[^\d.\-+\s,]/g, ' ');
    const numbers = cleaned.split(/[\s,]+/)
      .map(str => str.trim())
      .filter(str => str.length > 0)
      .map(str => parseFloat(str))
      .filter(num => !isNaN(num));

    return numbers;
  };

  const formatNumber = (num: number): string => {
    if (Number.isInteger(num)) {
      return num.toString();
    }

    const rounded = Math.round(num * 100000000) / 100000000;

    if (Math.abs(rounded) < 0.01 && rounded !== 0) {
      return rounded.toExponential(4);
    }

    return rounded.toString();
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const input = numberInput.trim();

    if (!input) {
      setError(t('errorEmpty'));
      setResults(null);
      return;
    }

    const numbers = parseNumbers(input);

    if (numbers.length === 0) {
      setError(t('errorInvalid'));
      setResults(null);
      return;
    }

    const sum = numbers.reduce((acc, num) => acc + num, 0);
    const count = numbers.length;
    const average = sum / count;

    setResults({
      average,
      sum,
      count,
      numbers
    });
  };

  const handleClear = () => {
    setNumberInput('');
    setResults(null);
    setError('');
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.1)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto', background: 'white', borderRadius: '1rem', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)' },
    {},
    { maxWidth: '700px' },
    { maxWidth: '800px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderTitleStyle = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.4rem' },
    { fontSize: '1.6rem' },
    { fontSize: '1.8rem' }
  );

  const formStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = getResponsiveStyle(
    {},
    { marginBottom: '1.25rem' },
    { marginBottom: '1.375rem' },
    { marginBottom: '1.5rem' }
  );

  const labelStyle = getResponsiveStyle(
    { display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#1e293b' },
    { fontSize: '0.9rem' },
    { fontSize: '0.925rem' },
    { fontSize: '0.95rem' }
  );

  const textareaStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontFamily: "'Courier New', monospace",
      transition: 'all 0.3s ease',
      resize: 'vertical' as const,
      minHeight: '120px',
      boxSizing: 'border-box' as const
    },
    { padding: '0.875rem', fontSize: '0.95rem' },
    { padding: '0.9375rem', fontSize: '0.975rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const inputHintStyle = getResponsiveStyle(
    { color: '#64748b', marginTop: '0.5rem', display: 'block' },
    { fontSize: '0.8rem' },
    { fontSize: '0.825rem' },
    { fontSize: '0.85rem' }
  );

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', marginTop: '1.5rem' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      flex: 1,
      fontWeight: 600,
      border: 'none',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white'
    },
    { padding: '0.875rem 1.5rem', fontSize: '0.95rem' },
    { padding: '0.9375rem 1.75rem', fontSize: '0.975rem' },
    { padding: '1rem 2rem', fontSize: '1rem' }
  );

  const clearButtonStyle = getResponsiveStyle(
    {
      flex: 1,
      fontWeight: 600,
      border: 'none',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569'
    },
    { padding: '0.875rem 1.5rem', fontSize: '0.95rem' },
    { padding: '0.9375rem 1.75rem', fontSize: '0.975rem' },
    { padding: '1rem 2rem', fontSize: '1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      borderTop: '3px solid #f59e0b'
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { margin: '0 0 1.5rem 0', color: '#92400e', display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.2rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.3rem' }
  );

  const resultGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1rem', marginBottom: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' },
    { gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }
  );

  const resultItemStyle = {
    background: 'white',
    padding: '1rem',
    borderRadius: '0.5rem',
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)'
  };

  const resultLabelStyle = getResponsiveStyle(
    { display: 'block', color: '#64748b', marginBottom: '0.5rem', fontWeight: 500 },
    { fontSize: '0.8rem' },
    { fontSize: '0.825rem' },
    { fontSize: '0.85rem' }
  );

  const resultValueStyle = getResponsiveStyle(
    { display: 'block', fontWeight: 700, color: '#1e293b' },
    { fontSize: '1.4rem' },
    { fontSize: '1.45rem' },
    { fontSize: '1.5rem' }
  );

  const resultValueHighlightStyle = getResponsiveStyle(
    { display: 'block', fontWeight: 700, color: '#2563eb' },
    { fontSize: '1.6rem' },
    { fontSize: '1.7rem' },
    { fontSize: '1.8rem' }
  );

  const numbersListStyle = {
    background: 'white',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem'
  };

  const numbersListH4Style = getResponsiveStyle(
    { margin: '0 0 0.75rem 0', color: '#92400e' },
    { fontSize: '0.95rem' },
    { fontSize: '0.975rem' },
    { fontSize: '1rem' }
  );

  const numbersDisplayStyle = {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '0.5rem'
  };

  const numberChipStyle = getResponsiveStyle(
    { background: '#dbeafe', color: '#1e40af', borderRadius: '0.25rem', fontWeight: 600 },
    { padding: '0.35rem 0.7rem', fontSize: '0.85rem' },
    { padding: '0.375rem 0.75rem', fontSize: '0.875rem' },
    { padding: '0.4rem 0.8rem', fontSize: '0.9rem' }
  );

  const calculationStepsStyle = {
    background: 'white',
    padding: '1.5rem',
    borderRadius: '0.5rem',
    marginTop: '1rem'
  };

  const calculationStepsH4Style = getResponsiveStyle(
    { margin: '0 0 1rem 0', color: '#92400e' },
    { fontSize: '0.95rem' },
    { fontSize: '0.975rem' },
    { fontSize: '1rem' }
  );

  const stepStyle = getResponsiveStyle(
    {
      padding: '0.75rem',
      background: '#fef3c7',
      borderLeft: '3px solid #f59e0b',
      marginBottom: '0.75rem',
      borderRadius: '0.25rem',
      fontFamily: "'Courier New', monospace"
    },
    { fontSize: '0.9rem' },
    { fontSize: '0.925rem' },
    { fontSize: '0.95rem' }
  );

  const errorMessageStyle = {
    background: '#fee2e2',
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '0.5rem',
    borderLeft: '4px solid #dc2626',
    marginTop: '1rem'
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.8, color: colors.textSecondary, marginLeft: '1.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const listItemStyle = {
    marginBottom: '0.75rem'
  };

  const strongStyle = {
    fontWeight: 600,
    color: colors.textPrimary
  };

  const refLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    borderBottom: `1px solid ${colors.primary}`,
    transition: 'all 0.3s ease'
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderTitleStyle}>{t('toolTitle')}</h2>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label htmlFor="number-inputs" style={labelStyle}>{t('inputLabel')}</label>
              <textarea
                style={textareaStyle}
                id="number-inputs"
                placeholder={t('inputPlaceholder')}
                value={numberInput}
                onChange={(e) => setNumberInput(e.target.value)}
                onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
              <span style={inputHintStyle}>{t('inputHint')}</span>
            </div>

            <div style={buttonGroupStyle}>
              <button
                type="submit"
                style={calculateButtonStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 5px 15px rgba(37, 99, 235, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {t('calculateButton')}
              </button>
              <button
                type="button"
                style={clearButtonStyle}
                onClick={handleClear}
                onMouseEnter={(e) => e.currentTarget.style.background = '#e2e8f0'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#f1f5f9'}
              >
                {t('clearButton')}
              </button>
            </div>
          </form>

          {(results || error) && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>{t('resultsTitle')}</h3>

              {error ? (
                <div style={errorMessageStyle}>{error}</div>
              ) : results && (
                <>
                  <div style={resultGridStyle}>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t('averageLabel')}</span>
                      <span style={resultValueHighlightStyle}>{formatNumber(results.average)}</span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t('sumLabel')}</span>
                      <span style={resultValueStyle}>{formatNumber(results.sum)}</span>
                    </div>
                    <div style={resultItemStyle}>
                      <span style={resultLabelStyle}>{t('countLabel')}</span>
                      <span style={resultValueStyle}>{results.count}</span>
                    </div>
                  </div>

                  <div style={numbersListStyle}>
                    <h4 style={numbersListH4Style}>{t('numbersUsedTitle')}</h4>
                    <div style={numbersDisplayStyle}>
                      {results.numbers.map((num, index) => (
                        <span key={index} style={numberChipStyle}>{formatNumber(num)}</span>
                      ))}
                    </div>
                  </div>

                  <div style={calculationStepsStyle}>
                    <h4 style={calculationStepsH4Style}>{t('calculationStepsTitle')}</h4>
                    <div style={stepStyle}>
                      {t('step1')}: {results.numbers.map(n => formatNumber(n)).join(' + ')}
                    </div>
                    <div style={stepStyle}>
                      {t('step2')}: {formatNumber(results.sum)}
                    </div>
                    <div style={stepStyle}>
                      {t('step3')}: {formatNumber(results.sum)} ÷ {results.count}
                    </div>
                    <div style={stepStyle}>
                      {t('step4')}: {formatNumber(results.average)}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('howToStep1Title')}</strong> {t('howToStep1Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('howToStep2Title')}</strong> {t('howToStep2Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('howToStep3Title')}</strong> {t('howToStep3Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('howToStep4Title')}</strong> {t('howToStep4Text')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph2')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph3')}</p>
        <p style={paragraphStyle}>{t('insightsParagraph4')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <p style={paragraphStyle}>{t('detailsParagraph1')}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('detailsPoint1Title')}</strong> {t('detailsPoint1Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('detailsPoint2Title')}</strong> {t('detailsPoint2Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('detailsPoint3Title')}</strong> {t('detailsPoint3Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('detailsPoint4Title')}</strong> {t('detailsPoint4Text')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('bestPracticesTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('practice1Title')}</strong> {t('practice1Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('practice2Title')}</strong> {t('practice2Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('practice3Title')}</strong> {t('practice3Text')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('practice4Title')}</strong> {t('practice4Text')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.2rem' : screenSize === 'tablet' ? '1.35rem' : '1.5rem', marginBottom: '0.5rem' }}>
            {t('faq1Question')}
          </h3>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.2rem' : screenSize === 'tablet' ? '1.35rem' : '1.5rem', marginBottom: '0.5rem' }}>
            {t('faq2Question')}
          </h3>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.2rem' : screenSize === 'tablet' ? '1.35rem' : '1.5rem', marginBottom: '0.5rem' }}>
            {t('faq3Question')}
          </h3>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.2rem' : screenSize === 'tablet' ? '1.35rem' : '1.5rem', marginBottom: '0.5rem' }}>
            {t('faq4Question')}
          </h3>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>
        <div>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.2rem' : screenSize === 'tablet' ? '1.35rem' : '1.5rem', marginBottom: '0.5rem' }}>
            {t('faq5Question')}
          </h3>
          <p style={paragraphStyle}>{t('faq5Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <a
              href="https://appstorespy.com/android-google-play/com.photon.healcalc-trends-revenue-statistics-downloads-ratings"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t('reference1')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://apps.apple.com/us/app/bmi-bmr-body-fat-calculator/id1326954500"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t('reference2')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a
              href="https://www.myfitnesspal.com"
              target="_blank"
              rel="noopener noreferrer"
              style={refLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t('reference3')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}